import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { 
    changePassword, 
    enable2FA, 
    disable2FA, 
    generate2FASecret, 
    getUser, 
    getSessions, 
    terminateSession, 
    terminateAllSessions, 
    getSecurityLogs 
} from '../services/api';
import { showSuccess, showError, showConfirmDanger, showPasswordPrompt } from '../services/swal';
import toast from 'react-hot-toast';

const AccountSettings = () => {
    const [user, setUser] = useState(JSON.parse(localStorage.getItem('user')));
    const [passwords, setPasswords] = useState({ current: '', new: '', confirm: '' });
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('security'); // 'security', 'sessions', 'logs'
    
    // 2FA States
    const [show2FAModal, setShow2FAModal] = useState(false);
    const [qrCode, setQrCode] = useState('');
    const [secret, setSecret] = useState('');
    const [otp, setOtp] = useState('');
    const [verifying2FA, setVerifying2FA] = useState(false);
    const [recoveryCodes, setRecoveryCodes] = useState([]);
    const [showRecoveryModal, setShowRecoveryModal] = useState(false);
    
    // Password Visibility States
    const [showPassCurrent, setShowPassCurrent] = useState(false);
    const [showPassNew, setShowPassNew] = useState(false);
    const [showPassConfirm, setShowPassConfirm] = useState(false);

    // Sessions & Logs
    const [sessions, setSessions] = useState([]);
    const [securityLogs, setSecurityLogs] = useState([]);
    const [logPagination, setLogPagination] = useState({ current: 1, total: 1 });

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const [userRes, sessionsRes, logsRes] = await Promise.all([
                getUser(),
                getSessions(),
                getSecurityLogs(1)
            ]);
            setUser(userRes.data);
            setSessions(sessionsRes.data);
            setSecurityLogs(logsRes.data.data);
            setLogPagination({ current: logsRes.data.current_page, total: logsRes.data.last_page });
            localStorage.setItem('user', JSON.stringify(userRes.data));
        } catch (err) {
            console.error("Failed to fetch account data", err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handlePasswordUpdate = async (e) => {
        e.preventDefault();
        if (passwords.new !== passwords.confirm) {
            showError('Mismatch', 'New passwords do not match.');
            return;
        }
        setLoading(true);
        try {
            await changePassword({
                current_password: passwords.current,
                new_password: passwords.new,
                new_password_confirmation: passwords.confirm
            });
            showSuccess('Security Updated', 'Your password has been changed successfully.');
            setPasswords({ current: '', new: '', confirm: '' });
            fetchData();
        } catch (err) {
            showError('Update Failed', err.response?.data?.message || 'Current password may be incorrect.');
        } finally {
            setLoading(false);
        }
    };

    const toggle2FA = async () => {
        if (user.google2fa_enabled) {
            const result = await showConfirmDanger(`Disable 2FA?`, `Your account security will be reduced.`);
            if (result.isConfirmed) {
                const { value: input } = await showPasswordPrompt('Verify Identity', 'Enter your Password or 2FA Code to continue.');
                if (input) {
                    try {
                        const payload = input.length === 6 && /^\d+$/.test(input) ? { otp: input } : { password: input };
                        await disable2FA(payload);
                        showSuccess('Security Updated', '2FA has been disabled.');
                        fetchData();
                    } catch (err) {
                        showError('Action Failed', err.response?.data?.message || 'Verification failed.');
                    }
                }
            }
        } else {
            setLoading(true);
            try {
                const res = await generate2FASecret();
                const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(res.data.qr_code_url)}`;
                setQrCode(qrUrl);
                setSecret(res.data.secret_key);
                setShow2FAModal(true);
            } catch (err) {
                showError('Setup Failed', 'Could not generate 2FA secret.');
            } finally {
                setLoading(false);
            }
        }
    };

    const handleVerify2FA = async () => {
        if (!otp) return;
        setVerifying2FA(true);
        try {
            const res = await enable2FA({ otp, secret });
            setRecoveryCodes(res.data.recovery_codes || []);
            setShow2FAModal(false);
            setShowRecoveryModal(true);
            fetchData();
        } catch (err) {
            showError('Verification Failed', err.response?.data?.message || 'Invalid code.');
        } finally {
            setVerifying2FA(false);
        }
    };

    const handleTerminateSession = async (id) => {
        const result = await showConfirmDanger('Terminate Session?', 'This device will be logged out.');
        if (result.isConfirmed) {
            try {
                await terminateSession(id);
                toast.success('Session revoked');
                setSessions(sessions.filter(s => s.id !== id));
            } catch (err) {
                toast.error('Failed to revoke session');
            }
        }
    };

    const handleTerminateAll = async () => {
        const result = await showConfirmDanger('Logout All Other Devices?', 'This will end all other active sessions.');
        if (result.isConfirmed) {
            try {
                await terminateAllSessions();
                toast.success('Other sessions ended');
                fetchData();
            } catch (err) {
                toast.error('Action failed');
            }
        }
    };

    const getPasswordStrength = (pass) => {
        if (!pass) return { score: 0, label: 'No Password', color: 'bg-light', textColor: 'text-muted' };
        let score = 0;
        if (pass.length >= 8) score += 25;
        if (/[A-Z]/.test(pass)) score += 25;
        if (/[0-9]/.test(pass)) score += 25;
        if (/[^A-Za-z0-9]/.test(pass)) score += 25;

        if (score <= 25) return { score, label: 'Weak', color: 'bg-danger', textColor: 'text-danger' };
        if (score <= 50) return { score, label: 'Fair', color: 'bg-warning', textColor: 'text-warning' };
        if (score <= 75) return { score, label: 'Good', color: 'bg-info', textColor: 'text-info' };
        return { score, label: 'Strong', color: 'bg-success', textColor: 'text-success' };
    };

    const strength = getPasswordStrength(passwords.new);

    if (loading && !user) return <Layout title="Security"><div className="text-center py-5"><div className="spinner-border text-primary"></div></div></Layout>;

    return (
        <Layout title="Account Security">
            <div className="account-security-container animate__animated animate__fadeIn">
                
                {/* Header & Quick Summary */}
                <div className="row g-4 mb-4">
                    <div className="col-12 col-lg-8">
                        <div className="card border-0 shadow-sm rounded-4 p-4 h-100 bg-white d-flex flex-column justify-content-center">
                            <h2 className="fw-bold mb-1">Security Dashboard</h2>
                            <p className="text-muted small mb-0">Monitor and manage your shop's defensive parameters and access tokens.</p>
                        </div>
                    </div>
                    <div className="col-12 col-lg-4">
                        <div className={`card border-0 shadow-sm rounded-4 p-4 h-100 text-center ${user.google2fa_enabled ? 'bg-success bg-opacity-10 text-success' : 'bg-warning bg-opacity-10 text-warning'}`}>
                            <div className="fs-1 mb-2"><i className={`bi ${user.google2fa_enabled ? 'bi-shield-fill-check' : 'bi-shield-fill-exclamation'}`}></i></div>
                            <h5 className="fw-bold mb-0">{user.google2fa_enabled ? 'Maximum Protection' : 'Enhanced Security'}</h5>
                            <span className="x-small fw-bold text-uppercase ls-1 opacity-75">{user.google2fa_enabled ? 'Multi-Factor Shield Active' : 'Enable 2FA for total safety'}</span>
                        </div>
                    </div>
                </div>

                {/* Stats Row */}
                <div className="row g-3 mb-4">
                    <div className="col-6 col-md-3">
                        <div className="card border-0 shadow-sm rounded-4 p-3 text-center bg-white hover-up transition-all">
                            <div className="text-primary fs-3 mb-1"><i className="bi bi-person-badge"></i></div>
                            <div className="x-small text-muted fw-bold text-uppercase">Role Access</div>
                            <div className="fw-bold">{user.role?.toUpperCase()}</div>
                        </div>
                    </div>
                    <div className="col-6 col-md-3">
                        <div className="card border-0 shadow-sm rounded-4 p-3 text-center bg-white hover-up transition-all">
                            <div className="text-info fs-3 mb-1"><i className="bi bi-display"></i></div>
                            <div className="x-small text-muted fw-bold text-uppercase">Active Nodes</div>
                            <div className="fw-bold">{sessions.length} Units</div>
                        </div>
                    </div>
                    <div className="col-6 col-md-3">
                        <div className="card border-0 shadow-sm rounded-4 p-3 text-center bg-white hover-up transition-all">
                            <div className="text-warning fs-3 mb-1"><i className="bi bi-clock-history"></i></div>
                            <div className="x-small text-muted fw-bold text-uppercase">Last Rotation</div>
                            <div className="fw-bold text-truncate">{user.last_password_change ? new Date(user.last_password_change).toLocaleDateString() : 'Initial'}</div>
                        </div>
                    </div>
                    <div className="col-6 col-md-3">
                        <div className="card border-0 shadow-sm rounded-4 p-3 text-center bg-white hover-up transition-all">
                            <div className="text-success fs-3 mb-1"><i className="bi bi-check2-circle"></i></div>
                            <div className="x-small text-muted fw-bold text-uppercase">Health Status</div>
                            <div className="fw-bold">{user.google2fa_enabled ? 'Healthy' : 'Vulnerable'}</div>
                        </div>
                    </div>
                </div>

                <div className="row g-4">
                    {/* Sidebar Tabs */}
                    <div className="col-12 col-xl-3">
                        <div className="security-sidebar card border-0 shadow-sm rounded-4 h-100 overflow-hidden">
                            <div className="nav flex-column nav-pills custom-pills p-3">
                                <button className={`nav-link ${activeTab === 'security' ? 'active' : ''}`} onClick={() => setActiveTab('security')}>
                                    <div className="nav-icon"><i className="bi bi-key-fill"></i></div>
                                    <div className="nav-text">
                                        <span className="fw-bold d-block">Credentials</span>
                                        <span className="x-small opacity-75">Password & 2FA</span>
                                    </div>
                                </button>
                                <button className={`nav-link ${activeTab === 'sessions' ? 'active' : ''}`} onClick={() => setActiveTab('sessions')}>
                                    <div className="nav-icon"><i className="bi bi-display"></i></div>
                                    <div className="nav-text">
                                        <span className="fw-bold d-block">Active Sessions</span>
                                        <span className="x-small opacity-75">{sessions.length} Devices active</span>
                                    </div>
                                </button>
                                <button className={`nav-link ${activeTab === 'logs' ? 'active' : ''}`} onClick={() => setActiveTab('logs')}>
                                    <div className="nav-icon"><i className="bi bi-shield-check"></i></div>
                                    <div className="nav-text">
                                        <span className="fw-bold d-block">Security Logs</span>
                                        <span className="x-small opacity-75">Activity history</span>
                                    </div>
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Main Content Area */}
                    <div className="col-12 col-xl-9">
                        
                        {/* Tab Content: Credentials */}
                        {activeTab === 'security' && (
                            <div className="animate__animated animate__fadeInUp">
                                <div className="card border-0 shadow-sm rounded-4 p-4 mb-4">
                                    <div className="row g-5">
                                        {/* Password Section */}
                                        <div className="col-12 col-lg-7 border-end-lg">
                                            <h5 className="fw-bold mb-4">Identity Verification</h5>
                                            <form onSubmit={handlePasswordUpdate}>
                                                <div className="mb-4">
                                                    <label className="form-label-custom">Current Security Password</label>
                                                    <div className="position-relative">
                                                        <i className="bi bi-lock position-absolute top-50 start-0 translate-middle-y ms-3 text-muted"></i>
                                                        <input type={showPassCurrent ? "text" : "password"} 
                                                            className="form-control-custom ps-5 pe-5" 
                                                            placeholder="••••••••" 
                                                            value={passwords.current} 
                                                            onChange={e => setPasswords({...passwords, current: e.target.value})}
                                                            required 
                                                        />
                                                        <button 
                                                            type="button"
                                                            className="btn btn-link position-absolute top-50 end-0 translate-middle-y me-2 text-muted shadow-none"
                                                            onClick={() => setShowPassCurrent(!showPassCurrent)}
                                                            style={{ textDecoration: 'none' }}
                                                        >
                                                            <i className={`bi bi-eye${showPassCurrent ? '-slash' : ''}`}></i>
                                                        </button>
                                                    </div>
                                                </div>

                                                <div className="row g-3 mb-4">
                                                    <div className="col-12">
                                                        <label className="form-label-custom">New Access Token</label>
                                                    <div className="position-relative">
                                                        <input type={showPassNew ? "text" : "password"} 
                                                            className="form-control-custom pe-5" 
                                                            placeholder="Enter new password" 
                                                            value={passwords.new} 
                                                            onChange={e => setPasswords({...passwords, new: e.target.value})}
                                                            required 
                                                        />
                                                        <button 
                                                            type="button"
                                                            className="btn btn-link position-absolute top-50 end-0 translate-middle-y me-2 text-muted shadow-none"
                                                            onClick={() => setShowPassNew(!showPassNew)}
                                                            style={{ textDecoration: 'none' }}
                                                        >
                                                            <i className={`bi bi-eye${showPassNew ? '-slash' : ''}`}></i>
                                                        </button>
                                                    </div>
                                                        {passwords.new && (
                                                            <div className="mt-2">
                                                                <div className="progress rounded-pill overflow-hidden" style={{ height: '6px' }}>
                                                                    <div className={`progress-bar ${strength.color}`} style={{ width: `${strength.score}%` }}></div>
                                                                </div>
                                                                <div className="d-flex justify-content-between mt-1">
                                                                    <span className={`x-small fw-bold ${strength.textColor}`}>{strength.label}</span>
                                                                    <span className="x-small text-muted">{strength.score}%</span>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="col-12">
                                                        <label className="form-label-custom">Confirm Access Token</label>
                                                        <div className="position-relative">
                                                            <input type={showPassConfirm ? "text" : "password"} 
                                                                className={`form-control-custom pe-5 ${passwords.confirm && passwords.new !== passwords.confirm ? 'is-invalid' : ''}`} 
                                                                placeholder="Repeat new password" 
                                                                value={passwords.confirm} 
                                                                onChange={e => setPasswords({...passwords, confirm: e.target.value})}
                                                                required 
                                                            />
                                                            <button 
                                                                type="button"
                                                                className="btn btn-link position-absolute top-50 end-0 translate-middle-y me-2 text-muted shadow-none"
                                                                onClick={() => setShowPassConfirm(!showPassConfirm)}
                                                                style={{ textDecoration: 'none' }}
                                                            >
                                                                <i className={`bi bi-eye${showPassConfirm ? '-slash' : ''}`}></i>
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                                
                                                <button type="submit" className="btn btn-primary px-4 py-2 fw-bold rounded-3" disabled={loading || !passwords.new || passwords.new !== passwords.confirm || strength.score < 50}>
                                                    {loading ? <span className="spinner-border spinner-border-sm me-2"></span> : <i className="bi bi-arrow-repeat me-2"></i>}
                                                    Commit Changes
                                                </button>
                                            </form>
                                        </div>

                                        {/* 2FA Section */}
                                        <div className="col-12 col-lg-5">
                                            <h5 className="fw-bold mb-4">MFA Shielding</h5>
                                            <div className="p-4 rounded-4 bg-light border text-center">
                                                <div className={`icon-shield-wrapper mb-3 ${user.google2fa_enabled ? 'text-success' : 'text-warning'}`}>
                                                    <i className={`bi ${user.google2fa_enabled ? 'bi-shield-check' : 'bi-shield-slash'} display-4`}></i>
                                                </div>
                                                <h6 className="fw-bold">{user.google2fa_enabled ? '2FA is Active' : 'Enable 2FA Protection'}</h6>
                                                <p className="x-small text-muted mb-4">
                                                    {user.google2fa_enabled 
                                                        ? 'Your account is fortified with biometric/OTP verification.' 
                                                        : 'Secure your business data from unauthorized login attempts.'}
                                                </p>
                                                <button className={`btn w-100 py-2 fw-bold rounded-3 ${user.google2fa_enabled ? 'btn-outline-danger' : 'btn-success'}`} onClick={toggle2FA}>
                                                    {user.google2fa_enabled ? 'Deactivate Shield' : 'Activate Shield'}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Tab Content: Sessions */}
                        {activeTab === 'sessions' && (
                            <div className="animate__animated animate__fadeInUp">
                                <div className="card border-0 shadow-sm rounded-4 p-4 mb-4">
                                    <div className="d-flex align-items-center justify-content-between mb-4">
                                        <h5 className="fw-bold mb-0">Authorized Nodes</h5>
                                        <button className="btn btn-sm btn-outline-danger px-3 py-2 rounded-3 fw-bold" onClick={handleTerminateAll}>
                                            <i className="bi bi-power me-2"></i>Logout Other Nodes
                                        </button>
                                    </div>

                                    <div className="row g-3">
                                        {sessions.map((session) => {
                                            const isCurrent = localStorage.getItem('token') === session.token || session.last_used_at === null;
                                            return (
                                                <div key={session.id} className="col-12 col-md-6">
                                                    <div className={`device-card p-3 rounded-4 border ${isCurrent ? 'border-primary bg-primary-subtle bg-opacity-10' : 'bg-white'}`}>
                                                        <div className="d-flex justify-content-between align-items-start mb-2">
                                                            <div className="d-flex align-items-center gap-3">
                                                                <div className="device-avatar bg-white shadow-sm rounded-3">
                                                                    <i className={`bi ${session.name?.toLowerCase().includes('mobile') ? 'bi-phone' : 'bi-display'} fs-4 text-primary`}></i>
                                                                </div>
                                                                <div>
                                                                    <div className="fw-bold text-dark">{session.name || 'Unknown Device'}</div>
                                                                    <div className="x-small text-muted">{isCurrent ? 'Current Session' : 'Active Remote'}</div>
                                                                </div>
                                                            </div>
                                                            {!isCurrent && (
                                                                <button className="btn btn-icon-sm text-danger" onClick={() => handleTerminateSession(session.id)} title="Revoke">
                                                                    <i className="bi bi-trash3"></i>
                                                                </button>
                                                            )}
                                                        </div>
                                                        <hr className="my-2 opacity-50" />
                                                        <div className="d-flex justify-content-between x-small">
                                                            <span className="text-muted">Last Seen</span>
                                                            <span className="fw-bold">{session.last_used_at ? new Date(session.last_used_at).toLocaleString() : 'Just now'}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Tab Content: Logs */}
                        {activeTab === 'logs' && (
                            <div className="animate__animated animate__fadeInUp">
                                <div className="card border-0 shadow-sm rounded-4 p-4 mb-4">
                                    <h5 className="fw-bold mb-4">Security Audit Trail</h5>
                                    <div className="timeline-wrapper">
                                        {securityLogs.length > 0 ? securityLogs.map((log, index) => (
                                            <div key={log.id} className="timeline-item d-flex gap-3 mb-4">
                                                <div className="timeline-marker position-relative">
                                                    <div className="marker-dot bg-primary"></div>
                                                    {index !== securityLogs.length - 1 && <div className="marker-line"></div>}
                                                </div>
                                                <div className="timeline-content flex-grow-1 p-3 rounded-4 bg-light border-start border-primary border-4 shadow-sm">
                                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                                        <span className="fw-bold text-dark">{log.action}</span>
                                                        <span className="x-small text-muted">{new Date(log.created_at).toLocaleString()}</span>
                                                    </div>
                                                    <div className="d-flex flex-wrap gap-x-4 gap-y-1">
                                                        <div className="x-small text-muted"><i className="bi bi-geo-alt me-1"></i>{log.ip_address}</div>
                                                        <div className="x-small text-muted text-truncate" style={{maxWidth:'250px'}} title={log.user_agent}><i className="bi bi-cpu me-1"></i>{log.user_agent}</div>
                                                    </div>
                                                </div>
                                            </div>
                                        )) : (
                                            <div className="text-center py-5">
                                                <i className="bi bi-journal-x display-4 text-muted"></i>
                                                <p className="mt-3 text-muted">No security logs recorded yet.</p>
                                            </div>
                                        )}
                                    </div>
                                    
                                    {logPagination.total > 1 && (
                                        <div className="d-flex justify-content-center mt-4">
                                            <div className="pagination-minimal d-flex align-items-center gap-3">
                                                <button className="btn btn-icon-sm rounded-circle border" disabled={logPagination.current === 1} 
                                                    onClick={() => fetchData(logPagination.current - 1)}>
                                                    <i className="bi bi-chevron-left"></i>
                                                </button>
                                                <span className="fw-bold x-small">Page {logPagination.current} of {logPagination.total}</span>
                                                <button className="btn btn-icon-sm rounded-circle border" disabled={logPagination.current === logPagination.total}
                                                    onClick={() => fetchData(logPagination.current + 1)}>
                                                    <i className="bi bi-chevron-right"></i>
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                    </div>
                </div>
            </div>

            {/* 2FA Modal: Glassmorphism Style */}
            {show2FAModal && (
                <div className="modal-glass-overlay">
                    <div className="modal-glass-card animate__animated animate__zoomIn">
                        <div className="d-flex justify-content-between align-items-center mb-4">
                            <h5 className="fw-bold m-0 text-white">Shield Configuration</h5>
                            <button className="btn-close btn-close-white" onClick={() => setShow2FAModal(false)}></button>
                        </div>
                        <div className="text-center">
                            <div className="qr-box-premium p-3 bg-white d-inline-block rounded-5 mb-4">
                                <img src={qrCode} alt="QR Code" style={{width:'180px'}} />
                            </div>
                            <div className="mb-4">
                                <span className="d-block text-white-50 x-small fw-bold mb-2 uppercase ls-1">AUTHENTICATOR SECRET</span>
                                <div className="manual-key-box p-3 rounded-4 text-white fw-bold mono d-flex align-items-center justify-content-between">
                                    {secret}
                                    <button className="btn btn-sm btn-link text-white-50 p-0 text-decoration-none" onClick={() => { navigator.clipboard.writeText(secret); toast.success('Key copied!'); }}>COPY</button>
                                </div>
                            </div>
                            <div className="mb-4 text-start">
                                <label className="form-label text-white-50 x-small fw-bold mb-2">VERIFICATION CODE</label>
                                <input 
                                    type="text" 
                                    className="form-control-custom text-center fs-2 fw-bold tracking-widest text-white" 
                                    style={{background:'rgba(255,255,255,0.1)', border:'1px solid rgba(255,255,255,0.2)'}}
                                    placeholder="000000" 
                                    maxLength="6"
                                    value={otp}
                                    onChange={e => setOtp(e.target.value.replace(/[^0-9]/g, ''))}
                                />
                            </div>
                            <button className="btn btn-light w-100 py-3 rounded-4 fw-bold fs-5 text-primary" onClick={handleVerify2FA} disabled={verifying2FA || otp.length < 6}>
                                {verifying2FA ? <span className="spinner-border spinner-border-sm me-2"></span> : <i className="bi bi-shield-check-fill me-2"></i>}
                                Complete Activation
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Recovery Codes Modal */}
            {showRecoveryModal && (
                <div className="modal-glass-overlay">
                    <div className="modal-glass-card animate__animated animate__zoomIn text-center" style={{maxWidth:'500px'}}>
                        <div className="icon-success-circle mb-3"><i className="bi bi-check-circle-fill display-4 text-success"></i></div>
                        <h4 className="text-white fw-bold">Fortification Complete</h4>
                        <p className="text-white-50 small mb-4">Your account is now shielded. Please save these emergency recovery codes in a secure offline location.</p>
                        
                        <div className="row g-2 mb-4">
                            {recoveryCodes.map((code, idx) => (
                                <div key={idx} className="col-6">
                                    <div className="p-2 rounded-3 text-white mono x-small fw-bold" style={{background:'rgba(255,255,255,0.1)'}}>{code}</div>
                                </div>
                            ))}
                        </div>
                        
                        <div className="d-grid gap-2">
                            <button className="btn btn-light py-3 rounded-4 fw-bold" onClick={() => {
                                const blob = new Blob([recoveryCodes.join('\n')], {type: 'text/plain'});
                                const url = window.URL.createObjectURL(blob);
                                const a = document.createElement('a');
                                a.href = url;
                                a.download = 'emergency-keys.txt';
                                a.click();
                            }}>
                                <i className="bi bi-download me-2"></i>Download Emergency Keys
                            </button>
                            <button className="btn btn-outline-light border-0 py-3 rounded-4 fw-bold" onClick={() => setShowRecoveryModal(false)}>
                                I've Secured My Keys
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <style>{`
                .account-security-container {
                    padding: 0;
                    font-family: 'DM Sans', sans-serif;
                }

                /* Sidebar Navigation Refined */
                .custom-pills .nav-link {
                    display: flex;
                    align-items: center;
                    gap: 15px;
                    padding: 18px 20px;
                    border-radius: 0;
                    color: var(--text-muted-color);
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                    position: relative;
                    background: transparent;
                    border: none;
                    text-align: left;
                    width: 100%;
                }
                .custom-pills .nav-link:hover {
                    color: var(--primary-color);
                    background: rgba(108, 93, 211, 0.04);
                }
                .custom-pills .nav-link.active {
                    color: var(--primary-color);
                    background: rgba(108, 93, 211, 0.08);
                }
                .custom-pills .nav-link.active::after {
                    content: '';
                    position: absolute;
                    left: 0;
                    top: 0;
                    bottom: 0;
                    width: 4px;
                    background: var(--primary-color);
                    box-shadow: 2px 0 10px rgba(108, 93, 211, 0.3);
                }
                .nav-icon {
                    width: 40px;
                    height: 40px;
                    background: white;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 1.2rem;
                    box-shadow: 0 4px 10px rgba(0,0,0,0.03);
                    transition: all 0.3s;
                }
                .custom-pills .nav-link.active .nav-icon {
                    background: var(--primary-color);
                    color: white;
                    box-shadow: 0 5px 15px rgba(108, 93, 211, 0.3);
                }

                /* Forms Refined */
                .form-control-custom {
                    background: #F8FAFD;
                    border: 2px solid #F1F4F9;
                    border-radius: 14px;
                    padding: 12px 18px;
                    font-weight: 500;
                    transition: all 0.3s;
                    width: 100%;
                }
                .form-control-custom:focus {
                    background: white;
                    border-color: var(--primary-color);
                    box-shadow: 0 0 0 4px rgba(108, 93, 211, 0.1);
                    outline: none;
                }
                .form-label-custom {
                    font-size: 11px;
                    font-weight: 800;
                    color: #8F9BBA;
                    text-transform: uppercase;
                    letter-spacing: 1px;
                    margin-bottom: 8px;
                    display: block;
                }

                /* Devices Refined */
                .device-card {
                    transition: all 0.3s;
                    border-color: #F1F4F9 !important;
                }
                .device-card:hover {
                    transform: translateY(-5px);
                    box-shadow: 0 15px 30px rgba(0,0,0,0.05) !important;
                    border-color: var(--primary-color) !important;
                }
                .device-avatar {
                    width: 48px;
                    height: 48px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                /* Timeline Refined */
                .timeline-wrapper {
                    padding-left: 10px;
                }
                .timeline-marker {
                    width: 12px;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    padding-top: 20px;
                }
                .marker-dot {
                    width: 12px;
                    height: 12px;
                    border-radius: 50%;
                    border: 3px solid white;
                    box-shadow: 0 0 0 2px var(--primary-color);
                    z-index: 1;
                }
                .marker-line {
                    width: 2px;
                    background: #E9EDF7;
                    flex-grow: 1;
                    margin-top: 5px;
                }

                /* Modals Glassmorphism */
                .modal-glass-overlay {
                    position: fixed;
                    top: 0; left: 0; width: 100vw; height: 100vh;
                    background: rgba(15, 15, 26, 0.8);
                    backdrop-filter: blur(12px);
                    z-index: 9999;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 20px;
                }
                .modal-glass-card {
                    background: rgba(255, 255, 255, 0.05);
                    border: 1px solid rgba(255, 255, 255, 0.1);
                    border-radius: 32px;
                    padding: 40px;
                    width: 100%;
                    max-width: 450px;
                    box-shadow: 0 25px 50px rgba(0,0,0,0.3);
                }
                .qr-box-premium { border: 8px solid rgba(255,255,255,0.05); }
                .manual-key-box { background: rgba(0,0,0,0.2); }
                .tracking-widest { letter-spacing: 12px; }

                /* Helpers */
                .x-small { font-size: 11px; }
                .mono { font-family: 'SFMono-Regular', Consolas, monospace; }
                .border-end-lg { border-right: 1px solid #F1F4F9; }
                @media (max-width: 991px) {
                    .border-end-lg { border-right: none; border-bottom: 1px solid #F1F4F9; padding-bottom: 3rem; }
                }

                [data-bs-theme="dark"] .card { background: #1A1A2E; border-color: #2D2D4A !important; }
                [data-bs-theme="dark"] .form-control-custom { background: #12121F; border-color: #2D2D4A; color: white; }
                [data-bs-theme="dark"] .bg-light { background: #12121F !important; }
                [data-bs-theme="dark"] .nav-icon { background: #12121F; }
                [data-bs-theme="dark"] .device-card { background: #12121F; border-color: #2D2D4A !important; }
                [data-bs-theme="dark"] .device-avatar { background: #1A1A2E !important; }
                [data-bs-theme="dark"] .timeline-content { background: #12121F; border-color: #2D2D4A !important; }
            `}</style>
        </Layout>
    );
};

export default AccountSettings;


